import { useQuery } from '@tanstack/react-query';
import type { AuditEntryDto, Page } from '@zenarty/shared';
import {
  Building2,
  KeyRound,
  LogIn,
  Pencil,
  Plus,
  ScanLine,
  ShieldAlert,
  ShieldCheck,
  UserCog,
  UserX,
  type LucideIcon,
} from 'lucide-react';
import { api } from '../api';
import { formatDateTime, timeAgo } from '../lib/format';
import { Skeleton } from './kit';

const LABELS: Record<string, string> = {
  'site.created': 'Site created',
  'site.updated': 'Site updated',
  'client.created': 'Client created',
  'client.updated': 'Client updated',
  'user.created': 'User created',
  'user.updated': 'User updated',
  'user.roles_changed': 'Roles changed',
  'user.disabled': 'User disabled',
  'user.enabled': 'User re-enabled',
  'user.password_reset': 'Password reset',
  'user.pin_reset': 'PIN reset',
  'user.password_changed': 'Password changed',
  'user.mfa_enrolled': 'Two-step sign-in set up',
  'user.mfa_reset': 'Two-step sign-in reset',
  'device.registered': 'Scanner registered',
  'device.enrolled': 'Scanner enrolled',
  'device.revoked': 'Scanner revoked',
  'auth.login': 'Signed in',
  'auth.device_login': 'Signed in on a scanner',
  'auth.logout': 'Signed out',
  'auth.failed_attempt': 'Failed sign-in attempt',
  'auth.refresh_token_reuse': 'Stolen-token alarm: sessions revoked',
  'operator.provisioned': 'Operator provisioned',
  'seed.demo_data': 'Demo data loaded',
};

export function actionLabel(action: string): string {
  return LABELS[action] ?? action.replace(/[._]/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
}

export function actionIcon(action: string): LucideIcon {
  if (action === 'auth.failed_attempt' || action === 'auth.refresh_token_reuse') return ShieldAlert;
  if (action.startsWith('auth.')) return LogIn;
  if (action.includes('mfa') || action.includes('password') || action.includes('pin'))
    return KeyRound;
  if (action.endsWith('disabled')) return UserX;
  if (action.startsWith('device.')) return ScanLine;
  if (action.startsWith('user.')) return UserCog;
  if (action.startsWith('operator.') || action.startsWith('seed.')) return Building2;
  if (action.endsWith('.created') || action.endsWith('.registered')) return Plus;
  if (action.endsWith('enrolled')) return ShieldCheck;
  return Pencil;
}

export function actorLabel(e: AuditEntryDto): string {
  if (e.actorName) return e.actorName;
  if (e.actorKind === 'platform') return 'Zenarty platform';
  if (e.actorKind === 'device') return 'Scanner';
  return 'System';
}

const IGNORED = new Set(['id', 'createdAt', 'updatedAt']);

function show(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  if (Array.isArray(v)) return v.length ? v.map((x) => show(x)).join(', ') : '—';
  if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') return String(v);
  return JSON.stringify(v);
}

/** Field-level changes between two audit snapshots. */
export function Diff({ before, after }: { before: unknown; after: unknown }) {
  const b = (before ?? {}) as Record<string, unknown>;
  const a = (after ?? {}) as Record<string, unknown>;
  const keys = [...new Set([...Object.keys(b), ...Object.keys(a)])].filter(
    (k) => !IGNORED.has(k) && JSON.stringify(b[k]) !== JSON.stringify(a[k]),
  );
  if (!keys.length) return null;
  const created = before === null || before === undefined;
  return (
    <div className="diff">
      {keys.slice(0, 12).map((k) => (
        <div key={k} className="diff-row">
          <span className="diff-key">{k}</span>
          {created ? (
            <span className="faint">—</span>
          ) : (
            <span className="diff-old">{show(b[k])}</span>
          )}
          <span className="diff-new">{show(a[k])}</span>
        </div>
      ))}
    </div>
  );
}

export function AuditTimeline({
  entries,
  showDiff = true,
}: {
  entries: AuditEntryDto[];
  showDiff?: boolean;
}) {
  if (!entries.length) return <p className="muted">No recorded activity yet.</p>;
  return (
    <div className="timeline">
      {entries.map((e) => {
        const Icon = actionIcon(e.action);
        const alarm = e.action === 'auth.failed_attempt' || e.action === 'auth.refresh_token_reuse';
        return (
          <div key={e.id} className="tl-item">
            <span
              className="tl-dot"
              style={
                alarm ? { background: 'var(--danger-soft)', color: 'var(--danger)' } : undefined
              }
            >
              <Icon aria-hidden />
            </span>
            <div style={{ minWidth: 0 }}>
              <div className="tl-title">{actionLabel(e.action)}</div>
              <div className="tl-meta" title={formatDateTime(e.createdAt)}>
                {actorLabel(e)} · {timeAgo(e.createdAt)}
                {e.ip ? ` · ${e.ip}` : ''}
              </div>
              {showDiff && (e.before || e.after) ? (
                <Diff before={e.before} after={e.after} />
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** Change history for one record (needs audit:read). */
export function EntityHistory({ entityId }: { entityId: string }) {
  const q = useQuery({
    queryKey: ['audit', 'entity', entityId],
    queryFn: () => api<Page<AuditEntryDto>>(`/audit-log?entityId=${entityId}&limit=25`),
  });
  if (q.isLoading) return <Skeleton height={60} />;
  if (q.error) return <p className="muted small">History is not available for your role.</p>;
  return <AuditTimeline entries={q.data?.items ?? []} />;
}
